import { useState, useEffect, useRef } from 'react';
import Modal from '../components/Modal';
import { useNavigate } from '../routing';
import { suppliersApi, statusBadge } from '../utils/api';
import { toast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { track, EVENTS } from '../utils/analytics';
import Icon from '../components/Icon';
import EmptyState from '../components/EmptyState';
import AliasesField from '../components/AliasesField';
import NewSupplierOptions, { HQ_ONLY_EXPLANATION } from '../components/NewSupplierOptions';
import StatInfo from '../components/StatInfo';
import { ViewAsMenuItems } from '../components/ViewAsMenu';

const EMPTY_FORM = { name: '', business_id: '', contact_name: '', phone: '', email: '', status: 'פעיל', payment_terms: '', payment_method: '', fixed_invoice_discount: '', fixed_invoice_discount_type: 'percent', has_deposit: false, has_purchase_tax: false, uses_delivery_notes: false, no_stable_sku: false, invoice_rules: '', aliases: [], hq_only: false, category_line_id: '' };

// `icon` is an Icon.jsx glyph name (not an emoji) so every payment method
// renders in the app's one line-icon language. Deliberately currency-neutral —
// `cash` is a banknote with no ₪/€/$ on it.
//
// It is a NAME, never something to render directly: it only means anything when
// passed to <Icon name={...} /> (or PaymentMethodIcon). Printing it into JSX
// puts the literal word "bank" on screen — which is exactly why a native
// <option> below shows the Hebrew label alone, since an <option> renders text
// only and cannot host an SVG.
export const PAYMENT_METHOD_OPTIONS = [
  { value: 'העברה בנקאית', icon: 'bank' },
  { value: 'כרטיס אשראי',  icon: 'card' },
  { value: 'צ׳ק',           icon: 'cheque' },
  { value: 'מזומן',         icon: 'cash' },
  { value: 'הוראת קבע',     icon: 'recurring' },
];

// Tolerant lookup: trim whitespace, normalize ASCII apostrophe (') and Hebrew
// geresh (׳) to a single canonical form so 'צ׳ק' and 'צ\'ק' both resolve to
// the same icon. Without this, a value typed by hand or imported from Excel
// could miss the lookup and render as no icon.
export const paymentMethodIcon = (m) => {
  if (!m) return '';
  const norm = (s) => String(s).trim().replace(/['׳]/g, '');
  const target = norm(m);
  return PAYMENT_METHOD_OPTIONS.find(o => norm(o.value) === target)?.icon || '';
};

// Icon element for a payment method — null when the method is unknown or blank,
// so call sites can render it inline next to the method name.
//
// `fallback` is empty on purpose: guessing a glyph for an unrecognized method
// draws, say, a credit card beside a value nobody chose, which reads as fact.
// Most call sites print the method name next to the icon, so rendering nothing
// loses no information. Where the icon stands alone, show a neutral placeholder
// (see MethodIcons in PaymentsPage.jsx) rather than passing a real glyph here.
export const PaymentMethodIcon = ({ method, size = 15, fallback = '' }) => {
  const name = paymentMethodIcon(method) || fallback;
  return name ? <Icon name={name} size={size} /> : null;
};

export function PaymentTermsInput({ value, onChange }) {
  const isNet  = value?.startsWith('שוטף');
  const netNum = isNet ? value.replace('שוטף', '').trim() : '';
  const type   = !value ? '' : isNet ? 'שוטף' : 'מזומן';

  const handleType = (t) => {
    if (t === '')           onChange('');
    else if (t === 'מזומן') onChange('מזומן');
    else                    onChange(netNum ? `שוטף ${netNum}` : 'שוטף');
  };

  const handleNum = (n) => onChange(n ? `שוטף ${n}` : 'שוטף');

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <select className="form-control" value={type} onChange={e => handleType(e.target.value)} style={{ flex: 1 }}>
        <option value="">— ללא —</option>
        <option value="מזומן">מזומן</option>
        <option value="שוטף">שוטף</option>
      </select>
      {type === 'שוטף' && (
        <input
          type="number"
          min="1"
          className="form-control"
          value={netNum}
          onChange={e => handleNum(e.target.value)}
          placeholder="30"
          style={{ width: 80, flexShrink: 0 }}
        />
      )}
    </div>
  );
}

// הרשאות גישה filter options (see SupplierManagementPage state).
const ACCESS_FILTERS = [
  { value: 'all', label: 'כל הספקים' },
  { value: 'hq', label: 'בעלים/מטה בלבד' },
  { value: 'everyone', label: 'גלוי לכולם' },
];

// One cell of the card's facts strip: small label over the value.
function SupplierFact({ label, value, wide = false }) {
  return (
    <div className={`supplier-card-fact${wide ? ' supplier-card-fact--wide' : ''}`}>
      <dt>{label}</dt>
      <dd>{value || '—'}</dd>
    </div>
  );
}

export default function SupplierManagementPage() {
  const { isAdmin } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [search, setSearch] = useState('');
  // הרשאות גישה filter (owner, 2026-10-03): plain state, deliberately NOT
  // persisted — every visit starts from "all" so nobody lands on a list that
  // silently hides half the suppliers.
  const [accessFilter, setAccessFilter] = useState('all');
  // Radio-group keys: one tab stop, arrows move the choice. RTL — the next
  // option sits to the LEFT, so ArrowLeft advances.
  const accessFilterRef = useRef(null);
  const onAccessFilterKey = (e) => {
    const step = { ArrowLeft: 1, ArrowDown: 1, ArrowRight: -1, ArrowUp: -1 }[e.key];
    if (!step) return;
    e.preventDefault();
    const i = ACCESS_FILTERS.findIndex(f => f.value === accessFilter);
    const next = (i + step + ACCESS_FILTERS.length) % ACCESS_FILTERS.length;
    setAccessFilter(ACCESS_FILTERS[next].value);
    accessFilterRef.current?.querySelectorAll('[role="radio"]')[next]?.focus();
  };
  const [pageMenuOpen, setPageMenuOpen] = useState(false);
  const [menuFor, setMenuFor] = useState(null);
  const menuTriggerRef = useRef(null);
  const menuPanelRef = useRef(null);
  const navigate = useNavigate();

  const closeCardMenu = (restoreFocus = true) => {
    const trigger = menuTriggerRef.current;
    setMenuFor(null);
    if (restoreFocus) requestAnimationFrame(() => trigger?.focus());
  };

  useEffect(() => {
    if (menuFor == null) return undefined;
    const frame = requestAnimationFrame(() => {
      menuPanelRef.current?.querySelector('[role="menuitem"]')?.focus();
    });
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') closeCardMenu();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', closeOnEscape);
    };
    // closeCardMenu intentionally reads the current trigger ref; including the
    // function would re-run this effect on every render while a menu is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuFor]);

  const handleCardMenuKeys = (event) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    const items = [...event.currentTarget.querySelectorAll('[role="menuitem"]')];
    if (!items.length) return;
    event.preventDefault();
    const current = items.indexOf(document.activeElement);
    const next = event.key === 'Home' ? 0
      : event.key === 'End' ? items.length - 1
        : event.key === 'ArrowDown' ? (current + 1) % items.length
          : (current - 1 + items.length) % items.length;
    items[next].focus();
  };

  const load = async () => {
    setLoading(true);
    try {
      // include_empty=true bypasses the strict purchase-doc filter applied to
      // the ספקים page so admins can manage every supplier — including ones
      // with no docs and ones whose only docs are קבלה/כרטסת/אחר.
      const res = await suppliersApi.getAll({ include_empty: true });
      setSuppliers(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const editOriginalTermsRef = useRef('');
  const startEdit = (s) => {
    editOriginalTermsRef.current = s.payment_terms || '';
    setEditingId(s.id);
    setEditForm({
      name: s.name || '',
      business_id: s.business_id || '',
      contact_name: s.contact_name || '',
      phone: s.phone || '',
      email: s.email || '',
      status: s.status || 'פעיל',
      payment_terms: s.payment_terms || '',
      payment_method: s.payment_method || '',
      // Blank when unset — server treats blank/0 as "no agreement".
      fixed_invoice_discount:      (s.fixed_invoice_discount || 0) > 0 ? s.fixed_invoice_discount : '',
      fixed_invoice_discount_type: s.fixed_invoice_discount_type || 'percent',
      invoice_rules:    s.invoice_rules || '',
      has_deposit:      !!s.has_deposit,
      has_purchase_tax: !!s.has_purchase_tax,
      uses_delivery_notes: !!s.uses_delivery_notes,
      no_stable_sku: !!s.no_stable_sku,
      hq_only: !!s.hq_only,
      aliases: s.aliases || [],
    });
  };

  const handleSave = async (id) => {
    if (!editForm.name.trim()) { toast('שם ספק הוא שדה חובה', 'error'); return; }
    try {
      await suppliersApi.update(id, { ...editForm, name: editForm.name.trim() });
      if ((editForm.payment_terms || '') !== editOriginalTermsRef.current) {
        track(EVENTS.PAYMENT_TERMS_CHANGED, { had_terms_before: !!editOriginalTermsRef.current, has_terms_after: !!(editForm.payment_terms || '').trim() });
      }
      toast('פרטי הספק עודכנו בהצלחה');
      setEditingId(null);
      load();
    } catch (err) {
      // 409 = another supplier of this restaurant already has the name; the
      // server's message names it. The edit stays open to fix the name.
      toast(err.response?.data?.error || 'שגיאה בעדכון הספק', 'error');
    }
  };

  // A nickname added or removed while editing is saved right away — on its
  // own, leaving the rest of the form's unsaved edits alone. On failure the
  // chips go back to what is stored. One save at a time (the field is
  // disabled meanwhile), and a reply that lands after the admin moved on to
  // another supplier only updates that supplier's card, never the open form.
  const [savingAliases, setSavingAliases] = useState(false);
  const editingIdRef = useRef(null);
  editingIdRef.current = editingId;
  const handleAliasesChange = async (id, aliases) => {
    const previous = editForm.aliases;
    const stillEditing = () => editingIdRef.current === id;
    setEditForm(f => ({ ...f, aliases }));
    setSavingAliases(true);
    try {
      const r = await suppliersApi.updateAliases(id, aliases);
      const saved = r.data.aliases;
      setSuppliers(list => list.map(s => (s.id === id ? { ...s, aliases: saved } : s)));
      if (stillEditing()) setEditForm(f => ({ ...f, aliases: saved }));
      // Judged on what the server kept: it drops a nickname that repeats
      // another or is the supplier's own name.
      if (saved.length > previous.length) toast('הכינוי נשמר');
      else if (saved.length < previous.length) toast('הכינוי הוסר');
      else toast('הכינוי כבר קיים או זהה לשם הספק — לא נוסף', 'error');
    } catch (err) {
      if (stillEditing()) setEditForm(f => ({ ...f, aliases: previous }));
      toast(err.response?.data?.error || 'שגיאה בשמירת הכינוי', 'error');
    } finally {
      setSavingAliases(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const r = await suppliersApi.create(addForm);
      // created:false = a supplier with this name (spaces and quote style
      // ignored) already exists here; nothing was saved. Refuse, and keep the
      // form open with what was typed — closing it used to drop the terms and
      // settings silently, as if they had been added.
      if (r?.data?.created === false) {
        toast(`ספק בשם "${r.data.name}" כבר קיים — לא נשמר`, 'error');
        return;
      }
      toast('ספק נוסף בהצלחה');
      setShowAddModal(false);
      setAddForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast(err.response?.data?.error || 'שגיאה בהוספת ספק', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await suppliersApi.delete(confirmDelete.id);
      toast(`ספק "${confirmDelete.name}" נמחק בהצלחה`);
      setConfirmDelete(null);
      load();
    } catch {
      toast('שגיאה במחיקת הספק', 'error');
    }
  };

  const ef = (field) => (e) => setEditForm(f => ({ ...f, [field]: e.target.value }));
  const af = (field) => (e) => setAddForm(f => ({ ...f, [field]: e.target.value }));

  const filtered = suppliers.filter(s => {
    if (accessFilter === 'hq' && !s.hq_only) return false;
    if (accessFilter === 'everyone' && s.hq_only) return false;
    if (!search) return true;
    return s.name.includes(search) ||
      (s.contact_name && s.contact_name.includes(search)) ||
      (s.phone && s.phone.includes(search));
  });
  const isFiltered = Boolean(search) || accessFilter !== 'all';

  return (
    <div>
      <div className="breadcrumb">
        <span className="link" onClick={() => navigate('/')}>ספקים</span>
        <Icon name="chevron-inline" size={13} className="breadcrumb-sep" />
        <span>ניהול ספקים</span>
      </div>

      <div className="page-title-row">
        <h1 className="page-title">ניהול ספקים</h1>
        <div className="page-title-actions">
          {isAdmin && <button className="btn btn-primary btn-sm" onClick={() => setShowAddModal(true)}>+ הוסף ספק</button>}
          {/* Same ⋮ as every page: the frequent action stays outside, the rest
              (and "צפייה כ…") live here. */}
          <div className="page-menu">
            <button
              type="button"
              className="page-menu-trigger"
              aria-label="פעולות נוספות"
              aria-expanded={pageMenuOpen}
              onClick={() => setPageMenuOpen(o => !o)}
            >⋮</button>
            {pageMenuOpen && (
              <>
                <div className="menu-backdrop" onClick={() => setPageMenuOpen(false)} />
                <div className="page-menu-panel">
                  {isAdmin && (
                    <button type="button" className="page-menu-item" onClick={() => { setPageMenuOpen(false); setShowAddModal(true); }}>
                      <Icon name="plus" size={15} /> הוסף ספק
                    </button>
                  )}
                  {isAdmin && (
                    <button type="button" className="page-menu-item" onClick={() => { setPageMenuOpen(false); navigate('/suppliers/merge'); }}>
                      <Icon name="merge" size={15} /> איחוד ספקים
                    </button>
                  )}
                  <ViewAsMenuItems onDone={() => setPageMenuOpen(false)} />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="action-row" style={{ marginBottom: 24 }}>
        <input
          className="filter-input supplier-search"
          placeholder="חיפוש לפי שם, איש קשר או טלפון..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {/* Designer pass: three short options are a segmented control, not a
            select — all visible, one tap. Same .scope-pill the /analysis scope
            switch uses, so it reads as part of the app. */}
        <div className="scope-pill supplier-access-filter" ref={accessFilterRef} role="radiogroup" aria-label="הרשאות גישה לספק">
          {ACCESS_FILTERS.map(f => (
            <button
              key={f.value}
              type="button"
              role="radio"
              aria-checked={accessFilter === f.value}
              tabIndex={accessFilter === f.value ? 0 : -1}
              className={`scope-seg${accessFilter === f.value ? ' is-active' : ''}`}
              onClick={() => setAccessFilter(f.value)}
              onKeyDown={onAccessFilterKey}
            >
              {f.value === 'hq' && <Icon name="lock" size={12} />} {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>
      ) : filtered.length === 0 ? (
        <EmptyState message={isFiltered ? 'לא נמצאו ספקים' : 'אין ספקים עדיין'} compact={isFiltered} />
      ) : (
        <div className="supplier-cards">
          {filtered.map(s => (
            <div className="supplier-card" key={s.id}>
              {editingId === s.id ? (
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--ink-muted)', marginBottom: 16 }}>עריכת ספק</div>
                  <div className="form-group">
                    <label>שם ספק *</label>
                    <input className="form-control" value={editForm.name} onChange={ef('name')} />
                  </div>
                  <div className="form-group">
                    <label>ח.פ / ע.מ</label>
                    <input className="form-control" value={editForm.business_id} onChange={ef('business_id')} placeholder="מספר עוסק / ח.פ" />
                  </div>
                  <AliasesField value={editForm.aliases} disabled={savingAliases} onChange={aliases => handleAliasesChange(s.id, aliases)} />
                  <div className="form-group">
                    <label>איש קשר</label>
                    <input className="form-control" value={editForm.contact_name} onChange={ef('contact_name')} />
                  </div>
                  <div className="form-group">
                    <label>טלפון</label>
                    <input className="form-control" type="tel" value={editForm.phone} onChange={ef('phone')} />
                  </div>
                  <div className="form-group">
                    <label>מייל</label>
                    <input className="form-control" type="email" value={editForm.email} onChange={ef('email')} />
                  </div>
                  <div className="form-group">
                    <label>סטטוס</label>
                    <select className="form-control" value={editForm.status} onChange={ef('status')}>
                      <option>פעיל</option>
                      <option>מושהה</option>
                      <option>לא פעיל</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>תנאי תשלום</label>
                    <PaymentTermsInput value={editForm.payment_terms || ''} onChange={v => setEditForm(f => ({ ...f, payment_terms: v }))} />
                  </div>
                  <div className="form-group">
                    <label>אמצעי תשלום</label>
                    <select className="form-control" value={editForm.payment_method || ''} onChange={ef('payment_method')}>
                      <option value="">— ללא —</option>
                      {PAYMENT_METHOD_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.value}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label title="הנחה קבועה מוסכמת על כל חשבונית מהספק. נערכת במסך הרכש (ניתוח רכש).">
                      הנחה קבועה לחשבונית
                    </label>
                    <div style={{ color: 'var(--ink-muted)', padding: '6px 0', fontSize: '0.9rem' }}>
                      {(editForm.fixed_invoice_discount || 0) > 0
                        ? `${editForm.fixed_invoice_discount}${editForm.fixed_invoice_discount_type === 'amount' ? ' ₪' : '%'}`
                        : '—'}
                      <span style={{ color: 'var(--ink-muted)', marginInlineStart: 8, fontSize: '0.8rem' }}>(נערך במסך הרכש)</span>
                    </div>
                  </div>
                  <div className="form-group" style={{ marginTop: 12 }}>
                    <label style={{ fontWeight: 600, color: 'var(--ink)', marginBottom: 8, display: 'block' }}>תוספות מחיר</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 400 }}>
                        <input
                          type="checkbox"
                          checked={!!editForm.has_deposit}
                          onChange={e => setEditForm(f => ({ ...f, has_deposit: e.target.checked }))}
                        />
                        פיקדון לפריט
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 400 }}>
                        <input
                          type="checkbox"
                          checked={!!editForm.has_purchase_tax}
                          onChange={e => setEditForm(f => ({ ...f, has_purchase_tax: e.target.checked }))}
                        />
                        מס קנייה לפריט
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 400 }}
                             title="כשמסומן, סכומי הרכש יחושבו לפי תעודות משלוח בלבד וחשבונית מרכזת לא תיכלל ברכש. בדף ספקים — תעודות שמקושרות לחשבונית מרכזת לא ייספרו פעמיים.">
                        <input
                          type="checkbox"
                          checked={!!editForm.uses_delivery_notes}
                          onChange={e => setEditForm(f => ({ ...f, uses_delivery_notes: e.target.checked }))}
                        />
                        עובד עם תעודות משלוח
                      </label>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontWeight: 400 }}
                             title="לספקים שלא מדפיסים מק״ט בתעודה — המספר בעמודת המק״ט הוא מספר שורה או מספר משלוח ולא קוד מוצר. כשמסומן, המערכת מזהה את המוצרים לפי השם ולא לפי המספר.">
                        <input
                          type="checkbox"
                          style={{ marginTop: 3 }}
                          checked={!!editForm.no_stable_sku}
                          onChange={e => setEditForm(f => ({ ...f, no_stable_sku: e.target.checked }))}
                        />
                        <span>
                          לספק אין מק״ט קבוע — לזהות מוצרים לפי שם
                          {/* The flag only affects documents saved from now on. Fixing the
                              codes already stored is a separate, deliberate step
                              (server/scripts/clear-unstable-skus.js), so say so here —
                              otherwise ticking the box looks like it did nothing. */}
                          <span style={{ display: 'block', fontSize: 12, color: 'var(--ink-muted)', marginTop: 2 }}>
                            חל על מסמכים חדשים בלבד. לתיקון מסמכי העבר יש להריץ ניקוי.
                          </span>
                        </span>
                      </label>
                    </div>
                  </div>
                  <NewSupplierOptions
                    name={editForm.name}
                    supplierId={s.id}
                    persistedHqOnly={!!s.hq_only}
                    businessId={editForm.business_id}
                    hqOnly={editForm.hq_only}
                    onHqOnlyChange={hq_only => setEditForm(f => ({ ...f, hq_only }))}
                    showCategory={false}
                  />
                  <div className="form-group" style={{ marginTop: 12 }}>
                    <label style={{ fontWeight: 600, color: 'var(--ink)', marginBottom: 4, display: 'block' }}>
                      כללים לקריאת חשבוניות
                    </label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={editForm.invoice_rules || ''}
                      onChange={e => setEditForm(f => ({ ...f, invoice_rules: e.target.value }))}
                      placeholder='לדוגמה: המחירים בפריטים כבר כוללים מע"מ — אל תוסיף שורת מע"מ בנפרד'
                      style={{ fontFamily: 'inherit', resize: 'vertical' }}
                    />
                    <div style={{ fontSize: 12, color: 'var(--ink-muted)', marginTop: 4 }}>
                      כל שורה = כלל אחד. הכללים נשלחים ל-Claude בעת קריאת חשבוניות מספק זה.
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <button className="btn btn-primary btn-sm" disabled={savingAliases} onClick={() => handleSave(s.id)}>שמור</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditingId(null)}>ביטול</button>
                  </div>
                </div>
              ) : (
                <div>
                  {/* Compact card (owner, 2026-10-03): identity + the three facts
                      people scan for; contact details live in edit mode only.
                      Edit is one visible tap (pencil); ⋮ keeps the destructive
                      action out of the way. */}
                  <div className="supplier-card-header">
                    <div className="supplier-card-ident">
                      <div className="supplier-card-name">{s.name}</div>
                      {s.aliases?.length > 0 && (
                        <div className="supplier-card-aliases" title="שמות כינוי">כינויים: {s.aliases.join(' · ')}</div>
                      )}
                      <div className="supplier-card-badges">
                        <span className={`badge ${statusBadge(s.status)}`}>{s.status}</span>
                        {!!s.hq_only && (
                          <span className="badge hq-only-badge">
                            <Icon name="lock" size={12} />
                            בעלים/מטה בלבד
                            <StatInfo label="בעלים/מטה בלבד" ariaLabel={`למה ${s.name} מוגבל לבעלים/מטה?`}>
                              {HQ_ONLY_EXPLANATION}
                            </StatInfo>
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="supplier-card-tools">
                    <button
                      type="button"
                      className="supplier-card-edit"
                      aria-label={`עריכת ${s.name}`}
                      title="עריכה"
                      onClick={() => startEdit(s)}
                    >
                      <Icon name="pencil" size={17} />
                    </button>
                    {isAdmin && (
                    <div className="page-menu supplier-card-menu">
                      <button
                        type="button"
                        className="page-menu-trigger supplier-card-menu-trigger"
                        aria-label={`פעולות עבור ${s.name}`}
                        aria-expanded={menuFor === s.id}
                        onClick={(event) => {
                          menuTriggerRef.current = event.currentTarget;
                          setMenuFor(current => current === s.id ? null : s.id);
                        }}
                      >⋮</button>
                      {menuFor === s.id && (
                        <>
                          <div className="menu-backdrop" onClick={() => closeCardMenu()} />
                          <div
                            ref={menuPanelRef}
                            className="page-menu-panel"
                            role="menu"
                            aria-label={`פעולות עבור ${s.name}`}
                            onKeyDown={handleCardMenuKeys}
                          >
                            <button
                              type="button"
                              className="page-menu-item"
                              role="menuitem"
                              style={{ color: 'var(--danger-ink)' }}
                              onClick={() => { setMenuFor(null); setConfirmDelete(s); }}
                            >
                              <Icon name="trash" size={15} /> מחק
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                    )}
                    </div>
                  </div>
                  <dl className="supplier-card-facts">
                    {/* ח.פ is the supplier's identity — first, on a row of its own. */}
                    <SupplierFact label="ח.פ / ע.מ" value={s.business_id} wide />
                    <SupplierFact label="תנאי תשלום" value={s.payment_terms} />
                    <SupplierFact label="אמצעי תשלום" value={s.payment_method
                      ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><PaymentMethodIcon method={s.payment_method} /> {s.payment_method}</span>
                      : ''} />
                  </dl>
                  {/* !! — the flags are SQLite 0/1, and a bare 0 renders as text. */}
                  {!!(s.has_deposit || s.has_purchase_tax) && (
                    <div className="supplier-card-extras">
                      תוספות מחיר: {[s.has_deposit && 'פיקדון', s.has_purchase_tax && 'מס קנייה'].filter(Boolean).join(' · ')}
                    </div>
                  )}
                  <div className="supplier-card-actions">
                    <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/supplier/${s.id}`)}>
                      <Icon name="documents" size={15} /> מסמכים ({s.invoice_count || 0})
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <Modal
          onClose={() => setShowAddModal(false)}
          closeOnBackdrop={false}
          ariaLabel="הוספת ספק חדש"
        >
          <h2 className="modal-title">הוספת ספק חדש</h2>
          <form onSubmit={handleAdd}>
            <div className="form-group">
              <label>שם ספק *</label>
              <input className="form-control" value={addForm.name} onChange={af('name')} required placeholder="שם הספק" />
            </div>
            <div className="form-group">
              <label>ח.פ / ע.מ</label>
              <input className="form-control" value={addForm.business_id} onChange={af('business_id')} placeholder="מספר עוסק / ח.פ" />
            </div>
            <AliasesField value={addForm.aliases} onChange={aliases => setAddForm(f => ({ ...f, aliases }))} />
            <div className="form-group">
              <label>איש קשר</label>
              <input className="form-control" value={addForm.contact_name} onChange={af('contact_name')} />
            </div>
            <div className="form-group">
              <label>טלפון</label>
              <input className="form-control" type="tel" value={addForm.phone} onChange={af('phone')} />
            </div>
            <div className="form-group">
              <label>מייל</label>
              <input className="form-control" type="email" value={addForm.email} onChange={af('email')} />
            </div>
            <div className="form-group">
              <label>סטטוס</label>
              <select className="form-control" value={addForm.status} onChange={af('status')}>
                <option>פעיל</option>
                <option>מושהה</option>
                <option>לא פעיל</option>
              </select>
            </div>
            <div className="form-group">
              <label>תנאי תשלום</label>
              <PaymentTermsInput value={addForm.payment_terms || ''} onChange={v => setAddForm(f => ({ ...f, payment_terms: v }))} />
            </div>
            <div className="form-group">
              <label>אמצעי תשלום</label>
              <select className="form-control" value={addForm.payment_method || ''} onChange={af('payment_method')}>
                <option value="">— ללא —</option>
                {PAYMENT_METHOD_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.value}</option>
                ))}
              </select>
            </div>
            {/* הנחה קבועה לחשבונית נקבעת במסך הרכש (ניתוח רכש), לא כאן. */}
            <div className="form-group" style={{ marginTop: 12 }}>
              <label style={{ fontWeight: 600, color: 'var(--ink)', marginBottom: 8, display: 'block' }}>תוספות מחיר</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 400 }}>
                  <input
                    type="checkbox"
                    checked={!!addForm.has_deposit}
                    onChange={e => setAddForm(f => ({ ...f, has_deposit: e.target.checked }))}
                  />
                  פיקדון לפריט
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 400 }}>
                  <input
                    type="checkbox"
                    checked={!!addForm.has_purchase_tax}
                    onChange={e => setAddForm(f => ({ ...f, has_purchase_tax: e.target.checked }))}
                  />
                  מס קנייה לפריט
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 400 }}
                       title="כשמסומן, סכומי הרכש יחושבו לפי תעודות משלוח בלבד וחשבונית מרכזת לא תיכלל ברכש. בדף ספקים — תעודות שמקושרות לחשבונית מרכזת לא ייספרו פעמיים.">
                  <input
                    type="checkbox"
                    checked={!!addForm.uses_delivery_notes}
                    onChange={e => setAddForm(f => ({ ...f, uses_delivery_notes: e.target.checked }))}
                  />
                  עובד עם תעודות משלוח
                </label>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontWeight: 400 }}
                       title="לספקים שלא מדפיסים מק״ט בתעודה — המספר בעמודת המק״ט הוא מספר שורה או מספר משלוח ולא קוד מוצר. כשמסומן, המערכת מזהה את המוצרים לפי השם ולא לפי המספר.">
                  <input
                    type="checkbox"
                    style={{ marginTop: 3 }}
                    checked={!!addForm.no_stable_sku}
                    onChange={e => setAddForm(f => ({ ...f, no_stable_sku: e.target.checked }))}
                  />
                  <span>
                    לספק אין מק״ט קבוע — לזהות מוצרים לפי שם
                    {/* See the edit modal's copy of this note — the flag is
                        forward-only; past documents need the cleanup script. */}
                    <span style={{ display: 'block', fontSize: 12, color: 'var(--ink-muted)', marginTop: 2 }}>
                      חל על מסמכים חדשים בלבד. לתיקון מסמכי העבר יש להריץ ניקוי.
                    </span>
                  </span>
                </label>
              </div>
            </div>
            <NewSupplierOptions
              name={addForm.name}
              businessId={addForm.business_id}
              hqOnly={addForm.hq_only}
              onHqOnlyChange={hq_only => setAddForm(f => ({ ...f, hq_only }))}
              categoryLineId={addForm.category_line_id}
              onCategoryLineChange={category_line_id => setAddForm(f => ({ ...f, category_line_id }))}
            />
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>ביטול</button>
              <button type="submit" className="btn btn-primary">שמור</button>
            </div>
          </form>
        </Modal>
      )}

      {confirmDelete && (
        <Modal
          onClose={() => setConfirmDelete(null)}
          size={420}
          ariaLabel="מחיקת ספק"
        >
          <h2 className="modal-title" style={{ color: 'var(--danger-ink)' }}>מחיקת ספק</h2>
          <p style={{ marginBottom: 8, color: 'var(--ink)' }}>
            האם אתה בטוח שברצונך למחוק את הספק <strong>{confirmDelete.name}</strong>?
          </p>
          {confirmDelete.invoice_count > 0 ? (
            <p style={{ marginBottom: 20, color: 'var(--danger-ink)', fontSize: '0.9rem', background: 'var(--danger-bg)', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--danger-line)' }}>
              <Icon name="alert" size={15} /> פעולה זו תמחק גם את כל {confirmDelete.invoice_count} המסמכים והפריטים הקשורים. לא ניתן לבטל פעולה זו.
            </p>
          ) : (
            <p style={{ marginBottom: 20, color: 'var(--ink-muted)', fontSize: '0.9rem' }}>פעולה זו אינה ניתנת לביטול.</p>
          )}
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setConfirmDelete(null)}>ביטול</button>
            <button className="btn btn-danger" onClick={handleDelete}>כן, מחק</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
